import React from 'react';

function ProModeContent() {
  return (
    <>
      <p>Pro Mode is an advanced feature in RepoPrompt designed to optimize the quality and efficiency of AI-generated code edits by intelligently distributing tasks across multiple specialized models.</p>

      <h3>What is Pro Mode?</h3>
      <p>Pro Mode breaks down your code editing requests into smaller, manageable tasks, each handled by the most appropriate AI model. This ensures high-quality edits, reduces costs, and mitigates API rate limits.</p>

      <p>Here's how Pro Mode functions:</p>
      <ul>
        <li><strong>Planning Model:</strong> This is the primary model you select in your chat session. It handles the overall planning and high-level decisions, such as creating new files, deleting files, and structuring complex edits. It breaks down larger tasks into concise, actionable changes for other models to execute efficiently.</li>
        <li><strong>Edit Models:</strong> These models receive specific file edits determined by the planning model. They work in parallel, enabling faster responses, reducing token usage, and controlling costs. Edit tasks are automatically routed to models based on file complexity and size.</li>
      </ul>

      <h3>Enabling Pro Mode</h3>
      <ol>
        <li>Begin a chat session by selecting files and composing your instructions.</li>
        <li>Click the "New AI Chat" button.</li>
        <li>Activate the "Pro" toggle within the chat panel to enable Pro Mode.</li>
      </ol>

      <h3>Configuring Pro Mode</h3>
      <ol>
        <li>Click the gear icon next to the model selection dropdown to open the configuration panel.</li>
        <li>Assign specific models for different types of edits:
          <ul>
            <li><strong>Simple Edits Model:</strong> For small, straightforward file edits.</li>
            <li><strong>Complex Edits Model:</strong> For intricate changes requiring higher precision.</li>
            <li><strong>Large File Model:</strong> Optimized for handling larger files effectively.</li>
          </ul>
        </li>
        <li>Set the "Large File Threshold" to define which edits qualify as large files.</li>
        <li>Pro users can also customize the model capabilities further through <strong>Model Overrides</strong>, allowing adjustments like enabling diff editing or streaming for individual models.</li>
      </ol>

      <h3>Why Use Pro Mode?</h3>
      <p>Pro Mode offers significant advantages, particularly when working with expensive or rate-limited AI models. By delegating the heavy lifting of detailed code edits to specialized, potentially less expensive models, you:</p>
      <ul>
        <li>Save on API usage costs by minimizing unnecessary tokens.</li>
        <li>Avoid exceeding rate limits of premium models.</li>
        <li>Ensure edits are consistently high-quality, accurate, and maintainable.</li>
      </ul>

      <h3>Technical Implementation</h3>
      <p>Pro Edit mode in RepoPrompt intelligently prioritizes models based on task complexity:</p>

      <h4>Optimal Model Selection</h4>
      <ul>
        <li><strong>For diff editing:</strong> The application maintains priority lists for different complexity levels, with sophisticated models like Claude 3.7 Sonnet, DeepSeek V3, and o3-mini-high receiving priority.</li>
        <li><strong>For whole file editing:</strong> Models like Gemini Flash 2.0 are prioritized for their efficiency with complete file rewrites.</li>
        <li><strong>Dynamic fallback:</strong> If preferred models aren't available, RepoPrompt will intelligently select alternatives from your configured providers.</li>
      </ul>

      <p>The configured models will process edits in parallel, significantly reducing overall response time and optimizing token usage across different API providers.</p>

      <h3>Important Considerations for Diff Editing</h3>
      <p>Diff editing—where the AI outputs only the changes rather than the whole file—requires models capable of detailed, precise modifications. Not all models handle this well, and subtle mistakes from weaker models can result in merging issues or broken code.</p>
      <p>We strongly recommend using powerful models like <strong>Claude Sonnet 3.5/3.7</strong> or <strong>DeepSeek V3</strong> for diff editing. These models are rigorously tested and proven effective at producing precise diffs that merge cleanly and accurately.</p>

      <div className="tip-box">
        <strong>Tip:</strong> Pro Mode is highly beneficial for complex editing tasks involving multiple files, extensive changes, or when managing API costs and quality standards is essential.
      </div>
    </>
  );
}

export default ProModeContent;
